import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useWallet } from '../contexts/ServiceContext';
import { DAOGovernanceService } from '../services/DAOGovernanceService';
import { ReputationService } from '../services/ReputationService';
import { sanitizeInput, sanitizeErrorMessage } from '../utils/security';
import './DAOVoting.css';

const DAOVoting = ({ onClose }) => {
  const { address: walletAddress } = useWallet();
  const [activeProposals, setActiveProposals] = useState([]);
  const [userVotes, setUserVotes] = useState({});
  const [userTokenBalance, setUserTokenBalance] = useState(0);
  const [userReputation, setUserReputation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [votingStates, setVotingStates] = useState({});
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [error, setError] = useState('');

  const daoService = new DAOGovernanceService();
  const reputationService = new ReputationService();

  // Vote options with descriptions
  const VOTE_OPTIONS = {
    UPHOLD: {
      label: 'Uphold Flag',
      description: 'Content violates guidelines and should be removed',
      icon: '✅',
      color: '#dc3545'
    },
    REJECT: {
      label: 'Reject Flag',
      description: 'Content does not violate guidelines',
      icon: '❌',
      color: '#28a745'
    },
    ESCALATE: {
      label: 'Escalate to Admins',
      description: 'Requires human review by moderators',
      icon: '⬆️',
      color: '#ffc107'
    }
  };

  // Proposal filters
  const FILTERS = {
    ALL: 'All Proposals',
    ACTIVE: 'Active Voting',
    ENDING_SOON: 'Ending Soon',
    HIGH_STAKES: 'High Stakes',
    MY_VOTES: 'My Votes'
  };

  useEffect(() => {
    if (walletAddress) {
      loadDAOData();
    }
  }, [walletAddress]);

  const loadDAOData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load active proposals
      const proposals = await daoService.getActiveProposals();
      setActiveProposals(proposals);

      // Load user's existing votes
      const votes = await daoService.getUserVotes(walletAddress);
      const voteMap = {};
      votes.forEach(vote => {
        voteMap[vote.proposalId] = vote;
      });
      setUserVotes(voteMap);

      // Load user's token balance and reputation
      const [tokenBalance, reputation] = await Promise.all([
        daoService.getTokenBalance(walletAddress),
        reputationService.getUserReputation(walletAddress)
      ]);

      setUserTokenBalance(tokenBalance);
      setUserReputation(reputation);
    } catch (error) {
      console.error('Error loading DAO data:', sanitizeErrorMessage(error));
      setError('Failed to load voting data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (proposalId, option, stakeAmount = 0) => {
    if (!walletAddress) {
      setError('Please connect your wallet to vote');
      return;
    }

    setVotingStates(prev => ({ ...prev, [proposalId]: true }));
    setError('');

    try {
      // Validate stake amount
      if (stakeAmount > userTokenBalance) {
        throw new Error('Insufficient token balance for stake');
      }

      // Submit vote
      const result = await daoService.vote(proposalId, {
        voter: walletAddress,
        option,
        stake: stakeAmount,
        timestamp: Date.now()
      });

      if (result.success) {
        // Update local state
        setUserVotes(prev => ({
          ...prev,
          [proposalId]: {
            proposalId,
            option,
            stake: stakeAmount,
            timestamp: Date.now()
          }
        }));

        // Update token balance
        setUserTokenBalance(prev => prev - stakeAmount);

        // Update reputation for voting
        await reputationService.updateReputation(walletAddress, 'DAO_VOTE', 10);

        // Reload proposals to get updated vote counts
        await loadDAOData();
      } else {
        throw new Error(result.error || 'Failed to submit vote');
      }
    } catch (error) {
      console.error('Error voting:', sanitizeErrorMessage(error));
      setError(sanitizeErrorMessage(error));
    } finally {
      setVotingStates(prev => ({ ...prev, [proposalId]: false }));
    }
  };

  const getFilteredProposals = () => {
    let filtered = [...activeProposals];

    switch (filter) {
      case 'ACTIVE':
        filtered = filtered.filter(p => p.status === 'ACTIVE');
        break;
      case 'ENDING_SOON':
        const oneDayFromNow = Date.now() + (24 * 60 * 60 * 1000);
        filtered = filtered.filter(p => p.endTime < oneDayFromNow);
        break;
      case 'HIGH_STAKES':
        filtered = filtered.filter(p => p.totalStake > 1000);
        break;
      case 'MY_VOTES':
        filtered = filtered.filter(p => userVotes[p.id]);
        break;
    }

    return filtered.sort((a, b) => {
      // Sort by urgency and stake
      const urgencyA = (a.endTime - Date.now()) / (1000 * 60 * 60); // Hours remaining
      const urgencyB = (b.endTime - Date.now()) / (1000 * 60 * 60);
      
      if (urgencyA < 24 && urgencyB >= 24) return -1;
      if (urgencyB < 24 && urgencyA >= 24) return 1;
      
      return b.totalStake - a.totalStake;
    });
  };

  const formatTimeRemaining = (endTime) => {
    const remaining = endTime - Date.now();
    if (remaining <= 0) return 'Ended';
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    return `${hours}h`;
  };

  const calculateVotingPower = (stakeAmount) => {
    if (!userReputation) return stakeAmount;
    
    // Reputation multiplier (max 2x for highest reputation)
    const reputationMultiplier = 1 + Math.min(1, userReputation.score / 1000);
    return Math.floor(stakeAmount * reputationMultiplier);
  };

  if (!walletAddress) {
    return (
      <div className="dao-voting-modal">
        <div className="dao-voting-container">
          <div className="dao-header">
            <h3>DAO Governance</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="dao-error">
            <p>Please connect your wallet to participate in DAO governance.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dao-voting-modal">
        <div className="dao-voting-container">
          <div className="dao-header">
            <h3>DAO Governance</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading governance proposals...</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredProposals = getFilteredProposals();

  return (
    <div className="dao-voting-modal">
      <div className="dao-voting-container">
        <div className="dao-header">
          <h3>🏛️ DAO Governance</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* User Stats */}
        <div className="user-stats">
          <div className="stat-card">
            <div className="stat-value">{userTokenBalance.toLocaleString()}</div>
            <div className="stat-label">ZNOOZINU Tokens</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{userReputation?.score || 0}</div>
            <div className="stat-label">Reputation</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{Object.keys(userVotes).length}</div>
            <div className="stat-label">Votes Cast</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          {Object.entries(FILTERS).map(([key, label]) => (
            <button
              key={key}
              className={`filter-tab ${filter === key ? 'active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="dao-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Proposals List */}
        <div className="proposals-list">
          {filteredProposals.length === 0 ? (
            <div className="no-proposals">
              <div className="no-proposals-icon">🗳️</div>
              <h4>No proposals found</h4>
              <p>There are currently no proposals matching your filter criteria.</p>
            </div>
          ) : (
            filteredProposals.map(proposal => (
              <ProposalCard
                key={proposal.id}
                proposal={proposal}
                userVote={userVotes[proposal.id]}
                userTokenBalance={userTokenBalance}
                userReputation={userReputation}
                onVote={handleVote}
                isVoting={votingStates[proposal.id]}
                calculateVotingPower={calculateVotingPower}
                formatTimeRemaining={formatTimeRemaining}
                VOTE_OPTIONS={VOTE_OPTIONS}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Proposal Card Component
const ProposalCard = ({
  proposal,
  userVote,
  userTokenBalance,
  userReputation,
  onVote,
  isVoting,
  calculateVotingPower,
  formatTimeRemaining,
  VOTE_OPTIONS
}) => {
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [stakeAmount, setStakeAmount] = useState(10);

  const timeRemaining = formatTimeRemaining(proposal.endTime);
  const isEnded = proposal.endTime <= Date.now();
  const hasVoted = !!userVote;

  const getWinningOption = () => {
    if (!proposal.votes || proposal.votes.length === 0) return null;
    
    const voteCounts = {};
    proposal.votes.forEach(vote => {
      voteCounts[vote.option] = (voteCounts[vote.option] || 0) + vote.stake;
    });

    return Object.entries(voteCounts).reduce((a, b) => 
      voteCounts[a[0]] > voteCounts[b[0]] ? a : b
    )[0];
  };

  const handleVoteSubmit = () => {
    onVote(proposal.id, selectedOption, stakeAmount);
    setShowVoteModal(false);
  };

  return (
    <div className="proposal-card">
      <div className="proposal-header">
        <div className="proposal-meta">
          <span className="proposal-type">Content Flag</span>
          <span className="proposal-time">{timeRemaining} remaining</span>
        </div>
        <div className="proposal-status">
          {isEnded ? (
            <span className="status-ended">Ended</span>
          ) : (
            <span className="status-active">Active</span>
          )}
        </div>
      </div>

      <div className="proposal-content">
        <h4>Flag Review: {proposal.flagData?.category}</h4>
        <p className="proposal-description">{proposal.flagData?.description}</p>
        
        <div className="content-preview">
          <div className="content-type">Content ID: {proposal.contentId}</div>
          <div className="flag-severity">
            Severity: <span className={`severity-${proposal.flagData?.severity?.toLowerCase()}`}>
              {proposal.flagData?.severity}
            </span>
          </div>
        </div>
      </div>

      <div className="proposal-stats">
        <div className="stat">
          <span className="stat-label">Total Stake:</span>
          <span className="stat-value">{proposal.totalStake || 0} ZNOOZINU</span>
        </div>
        <div className="stat">
          <span className="stat-label">Participants:</span>
          <span className="stat-value">{proposal.votes?.length || 0}</span>
        </div>
      </div>

      {/* Vote Results */}
      {proposal.votes && proposal.votes.length > 0 && (
        <div className="vote-results">
          {Object.entries(VOTE_OPTIONS).map(([option, config]) => {
            const optionVotes = proposal.votes.filter(v => v.option === option);
            const optionStake = optionVotes.reduce((sum, v) => sum + v.stake, 0);
            const percentage = proposal.totalStake > 0 ? (optionStake / proposal.totalStake) * 100 : 0;

            return (
              <div key={option} className="vote-option-result">
                <div className="vote-option-header">
                  <span className="vote-option-icon">{config.icon}</span>
                  <span className="vote-option-label">{config.label}</span>
                  <span className="vote-percentage">{percentage.toFixed(1)}%</span>
                </div>
                <div className="vote-progress-bar">
                  <div 
                    className="vote-progress-fill"
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: config.color
                    }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* User Vote Status */}
      {hasVoted ? (
        <div className="user-vote-status">
          <div className="voted-indicator">
            <span className="voted-icon">✓</span>
            You voted: <strong>{VOTE_OPTIONS[userVote.option]?.label}</strong>
          </div>
          <div className="vote-details">
            Stake: {userVote.stake} ZNOOZINU | 
            Power: {calculateVotingPower(userVote.stake)}
          </div>
        </div>
      ) : !isEnded ? (
        <div className="vote-actions">
          <button
            className="vote-btn"
            onClick={() => setShowVoteModal(true)}
            disabled={isVoting || userTokenBalance < 10}
          >
            {isVoting ? (
              <>
                <span className="loading-spinner"></span>
                Voting...
              </>
            ) : (
              '🗳️ Cast Vote'
            )}
          </button>
        </div>
      ) : (
        <div className="proposal-ended">
          <span className="ended-icon">🏁</span>
          Voting has ended
          {getWinningOption() && (
            <div className="winning-option">
              Winner: {VOTE_OPTIONS[getWinningOption()]?.label}
            </div>
          )}
        </div>
      )}

      {/* Vote Modal */}
      {showVoteModal && (
        <div className="vote-modal">
          <div className="vote-modal-content">
            <div className="vote-modal-header">
              <h4>Cast Your Vote</h4>
              <button 
                className="close-btn"
                onClick={() => setShowVoteModal(false)}
              >×</button>
            </div>

            <div className="vote-options">
              {Object.entries(VOTE_OPTIONS).map(([option, config]) => (
                <label key={option} className="vote-option">
                  <input
                    type="radio"
                    name="voteOption"
                    value={option}
                    checked={selectedOption === option}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  />
                  <div className="vote-option-content">
                    <div className="vote-option-main">
                      <span className="vote-option-icon">{config.icon}</span>
                      <span className="vote-option-label">{config.label}</span>
                    </div>
                    <div className="vote-option-description">{config.description}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="stake-input">
              <label>Stake Amount (ZNOOZINU):</label>
              <input
                type="number"
                min="10"
                max={userTokenBalance}
                value={stakeAmount}
                onChange={(e) => setStakeAmount(parseInt(e.target.value) || 10)}
              />
              <div className="stake-info">
                Voting Power: {calculateVotingPower(stakeAmount)} 
                (includes {userReputation?.score || 0} reputation bonus)
              </div>
            </div>

            <div className="vote-modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowVoteModal(false)}
              >
                Cancel
              </button>
              <button
                className="submit-vote-btn"
                onClick={handleVoteSubmit}
                disabled={!selectedOption || stakeAmount < 10 || stakeAmount > userTokenBalance}
              >
                Submit Vote ({stakeAmount} ZNOOZINU)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DAOVoting;
